import type {CmLogger, LoggerDomain, LogLevel} from "@cm/log"
import {BrowserLogger} from "#browser-log/browser-logger"
import type {LoggerConfig} from "#browser-log/storage"
import {loggerConfig} from "#browser-log/storage"

/**
 * Factory for creating domain-specific loggers with shared configuration.
 * Uses localStorage for persisting log level settings.
 */
export class LoggerFactory {
    private readonly loggers = new Map<LoggerDomain, CmLogger>()

    constructor(private readonly config: LoggerConfig = loggerConfig) {}

    /**
     * Get or create a logger for the specified domain.
     * The same logger instance is returned for repeated calls with the same domain.
     */
    getLogger(domain: LoggerDomain): CmLogger {
        let logger = this.loggers.get(domain)
        if (!logger) {
            logger = new BrowserLogger(domain, () => this.config.getEffectiveLevel(domain))
            this.loggers.set(domain, logger)
        }
        return logger
    }

    /**
     * Set the global log level.
     */
    setGlobalLevel(level: LogLevel): void {
        this.config.setGlobalLevel(level)
    }

    /**
     * Get the global log level.
     */
    getGlobalLevel(): LogLevel {
        return this.config.getGlobalLevel()
    }

    /**
     * Set the log level for a specific domain.
     */
    setDomainLevel(domain: LoggerDomain, level: LogLevel): void {
        this.config.setDomainLevel(domain, level)
    }

    /**
     * Clear the log level override for a domain (reverts to global).
     */
    clearDomainLevel(domain: LoggerDomain): void {
        this.config.clearDomainLevel(domain)
    }

    /**
     * Get the current configuration for debugging.
     */
    getConfig(): {global: LogLevel; domains: Partial<Record<LoggerDomain, LogLevel>>} {
        return {
            global: this.config.getGlobalLevel(),
            domains: this.config.getAllDomainLevels(),
        }
    }

    /**
     * Reset all configuration to defaults.
     */
    reset(): void {
        this.config.reset()
    }

    /**
     * Subscribe to configuration changes. Returns an unsubscribe function.
     */
    subscribe(listener: () => void): () => void {
        return this.config.subscribe(listener)
    }

    /**
     * Get a configuration snapshot suitable for transmission to a worker.
     */
    getSnapshot(): {global: LogLevel; domains: Partial<Record<LoggerDomain, LogLevel>>} {
        return this.getConfig()
    }

    /**
     * Apply a configuration snapshot to the underlying config (in-memory only).
     */
    applySnapshot(snapshot: {global: LogLevel; domains: Partial<Record<LoggerDomain, LogLevel>>}): void {
        this.config.applySnapshot(snapshot)
    }
}

/**
 * Default factory instance using the default LoggerConfig.
 */
export const loggerFactory = new LoggerFactory()
